import React, { useEffect, useRef, useState } from 'react';
import { ddMmYyyyToIso, emitDateChange, isoToDdMmYyyy } from './dateInputUtils';
import './DateInput.css';

/**
 * Fecha visible DD/MM/YYYY; value/onChange en YYYY-MM-DD (mismo contrato que input type="date").
 */
export default function DateInput({
  value = '',
  onChange,
  name,
  id,
  className = '',
  size,
  isInvalid = false,
  required = false,
  disabled = false,
  readOnly = false,
  min,
  max,
  placeholder = 'DD/MM/AAAA',
  title,
  plain = false,
}) {
  const iso = String(value || '').slice(0, 10);
  const [text, setText] = useState(() => isoToDdMmYyyy(iso));
  const [focused, setFocused] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!focused) setText(isoToDdMmYyyy(iso));
  }, [iso, focused]);

  const commit = (raw) => {
    const next = ddMmYyyyToIso(raw);
    if (next === null) {
      setText(isoToDdMmYyyy(iso));
      return;
    }
    setText(isoToDdMmYyyy(next));
    if (next !== iso) emitDateChange(onChange, next, name);
  };

  const openPicker = () => {
    if (disabled || readOnly) return;
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* fallback: click nativo */
      }
    }
    el.click();
  };

  const fieldClass = [
    plain ? '' : 'form-control',
    !plain && size === 'sm' ? 'form-control-sm' : '',
    isInvalid ? 'is-invalid' : '',
    'pt-date-input__field',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapClass = [
    'pt-date-input',
    size === 'sm' ? 'pt-date-input--sm' : '',
    isInvalid ? 'is-invalid' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapClass}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        className={fieldClass}
        value={focused ? text : isoToDdMmYyyy(iso) || text}
        placeholder={placeholder}
        title={title || 'Fecha DD/MM/AAAA'}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        onFocus={() => {
          setFocused(true);
          setText(isoToDdMmYyyy(iso) || text);
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setFocused(false);
          commit(text);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(text);
          }
        }}
      />
      <button
        type="button"
        className="pt-date-input__cal"
        tabIndex={-1}
        disabled={disabled || readOnly}
        aria-label="Abrir calendario"
        onClick={openPicker}
      >
        <i className="bi bi-calendar3" aria-hidden="true" />
      </button>
      <input
        ref={pickerRef}
        type="date"
        className="pt-date-input__native"
        tabIndex={-1}
        aria-hidden="true"
        value={iso}
        min={min}
        max={max}
        disabled={disabled || readOnly}
        onChange={(e) => {
          const next = e.target.value || '';
          setText(isoToDdMmYyyy(next));
          emitDateChange(onChange, next, name);
        }}
      />
    </div>
  );
}
